import React, { useState, useEffect, useCallback } from "react";
import { Card } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectContentItemById } from "../../selectors";
import {
  fetchContentItemById,
  removeContentItem,
  saveContentItem,
} from "../../thunks.js";

import ContentItemForm from "./ContentItemForm";

const messages = defineMessages({
  delete: {
    id: "modules.UrlEntry.components.ContentItemDetail.delete",
    description: "Delete Item message",
    defaultMessage: "Are you sure you want to delete this content item?",
  },
});

const ContentItemEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const contentItem = useSelector((state) =>
    selectContentItemById(state, { identifier }),
  );

  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchContentItemById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !contentItem) {
      fetchData();
    }
  }, [contentItem, identifier, dispatch]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true);

      try {
        await dispatch(
          saveContentItem(values, contentItem ? contentItem.id : undefined),
        );
        navigate("/arrivideo#content", { replace: true });
      } catch (err) {
        setError(`Er ging iets fout bij het opslaan: ${err}`);
      } finally {
        setSubmitting(false);
      }
    },
    [contentItem, navigate],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};
      const requiredFields = ["type", "content"];
      requiredFields.forEach((field) => {
        if (!values[field])
          errors[field] = intl.formatMessage(genericTranslations.required);
      });
      return errors;
    },
    [intl],
  );

  const handleRemoveContentItem = async () => {
    try {
      await dispatch(removeContentItem(contentItem.id));
      navigate("/arrivideo#content", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!contentItem && identifier) {
    return (
      <strong>{intl.formatMessage(genericTranslations.nothingFound)}</strong>
    );
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        Content{" "}
        {contentItem
          ? intl.formatMessage(genericTranslations.edit).toLowerCase()
          : intl.formatMessage(genericTranslations.add).toLowerCase()}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={contentItem || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={ContentItemForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={`${intl.formatMessage(genericTranslations.delete)} content`}
          bodyText={intl.formatMessage(messages.delete)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveContentItem}
        />
      </Card.Body>
    </Card>
  );
};

export default ContentItemEditor;
